import { z } from 'zod';
/**
 * One module owns every locale vocabulary we speak (PLAN.md §17).
 *
 * They genuinely differ and mixing them is a silent failure:
 *   app code   `tw`/`cn` — what appears in ?lang= and our own storage
 *   CRM        `zh_Hant`/`zh` — the `_locale` on /rest and /client-api (supported:
 *                              en, vi, zh_Hant, th, zh, ja, ko)
 *   Didit      `zh-TW`/`zh-CN` — the `language` field on POST /v3/session/
 *   Turnstile  `zh-tw`/`zh-cn` — the widget's `language` render option
 *
 * Traditional and Simplified are separate locales, not one "Chinese": the
 * scripts and much of the vocabulary differ. `tw` is our code for Traditional,
 * read in Taiwan, Hong Kong and Macau; `cn` is Simplified, read in mainland
 * China and Singapore. Every vendor spells the pair differently and none of
 * them will tell us we picked the wrong one.
 */
export const APP_LOCALES = ['en', 'th', 'vi', 'tw', 'cn', 'ja', 'ko'];
export const AppLocale = z.enum(APP_LOCALES);
export const DEFAULT_LOCALE = 'en';
const TO_CRM = {
    en: 'en',
    th: 'th',
    vi: 'vi',
    // `zh_Hant` is the CRM's Traditional. Its Simplified is bare `zh`, not
    // `zh_Hans`. Supported set: en, vi, zh_Hant, th, zh, ja.
    tw: 'zh_Hant',
    cn: 'zh',
    ja: 'ja',
    // Plain ISO 639-1, which is what `step1.php:125` falls through to for every
    // language but Traditional Chinese — the app code *is* the CRM locale unless
    // the CRM spells it differently, and for Korean it does not.
    ko: 'ko',
};
/**
 * Didit's own codes, which are case-sensitive and validated — anything outside
 * their table is a 400, not a fallback.
 *
 * The two Chinese entries are the regional variants Didit documents and tells
 * integrators to prefer. Sending generic `zh` is accepted but means Simplified,
 * so a Traditional applicant read their verification in the wrong script.
 */
const TO_DIDIT = {
    en: 'en',
    th: 'th',
    vi: 'vi',
    tw: 'zh-TW',
    cn: 'zh-CN',
    ja: 'ja',
    ko: 'ko',
};
/** App locale → CRM `_locale`. */
export function toCrmLocale(locale) {
    return TO_CRM[locale];
}
/** App locale → Didit `language`. */
export function toDiditLanguage(locale) {
    return TO_DIDIT[locale];
}
/**
 * Turnstile's codes are lowercase, and it has no two-letter form for
 * Traditional Chinese — `zh-tw` is the only way to ask for it. An unknown code
 * is not an error either: the widget quietly falls back, which is how `tw` and
 * `cn` were rendering an English challenge to the applicants least able to read
 * one.
 */
const TO_TURNSTILE = {
    en: 'en',
    th: 'th',
    vi: 'vi',
    tw: 'zh-tw',
    cn: 'zh-cn',
    ja: 'ja',
    // Turnstile documents both `ko` and `ko-kr`; the two-letter form is the one
    // that matches every other entry here.
    ko: 'ko',
};
/** App locale → Turnstile widget `language`. */
export function toTurnstileLanguage(locale) {
    return TO_TURNSTILE[locale];
}
/**
 * Resolve an untrusted locale hint (?lang=, cookie, Accept-Language) to a
 * supported locale. Never throws — falls back rather than failing a request.
 */
export function resolveLocale(input, fallback = DEFAULT_LOCALE) {
    if (!input)
        return fallback;
    const raw = input.trim().toLowerCase();
    if (!raw || raw === 'undefined' || raw === 'null')
        return fallback;
    const parsed = AppLocale.safeParse(raw);
    if (parsed.success)
        return parsed.data;
    // Accept-Language style: `zh-Hant-TW,zh;q=0.9` → take the first tag
    const first = raw.split(',')[0]?.split(';')[0]?.trim() ?? '';
    // Chinese needs the script split: Traditional markers (Hant, TW/HK/MO) win,
    // everything else under `zh` — Hans, CN, SG, or bare `zh` — is Simplified.
    if (first.startsWith('zh')) {
        return /hant|[-_](tw|hk|mo)/.test(first) ? 'tw' : 'cn';
    }
    const base = first.split(/[-_]/)[0] ?? '';
    const byBase = AppLocale.safeParse(base);
    return byBase.success ? byBase.data : fallback;
}
//# sourceMappingURL=locale.js.map